import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import myAxios from '../AxiosUrl';
import styles from './AICalling.module.css';

/**
 * AI Calling → Do-not-call: the shop's list. Customers who said "don't call me" on a call are added
 * automatically (no more marketing calls; their own order confirmations still happen). The shop can
 * add numbers — "marketing" or "all AI calls" — and remove them.
 */

const errMsg = (err, fallback) => err?.response?.data?.message || err?.message || fallback;
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

function DoNotCall() {
    const [data, setData] = useState({ rows: [], page: 1, pages: 1, total: 0 });
    const [q, setQ] = useState('');
    const [mobile, setMobile] = useState('');
    const [scope, setScope] = useState('marketing');
    const [busy, setBusy] = useState(false);

    const load = useCallback(
        async (page = 1) => {
            try {
                const res = await myAxios.get('call/optouts', { params: { page, q: q.trim() } });
                setData(res.data);
            } catch (err) {
                toast.error(errMsg(err, 'Could not load the list'));
            }
        },
        [q]
    );
    useEffect(() => {
        const t = setTimeout(() => load(1), 300);
        return () => clearTimeout(t);
    }, [load]);

    const add = async () => {
        if (!/^01[3-9]\d{8}$/.test(mobile.trim())) return toast.error('Enter a mobile number (01XXXXXXXXX)');
        try {
            setBusy(true);
            const res = await myAxios.post('call/optouts', { mobile: mobile.trim(), scope });
            toast.success(res.data?.message || 'Added');
            setMobile('');
            load(1);
        } catch (err) {
            toast.error(errMsg(err, 'Could not add'));
        } finally {
            setBusy(false);
        }
    };
    const remove = async (r) => {
        if (!window.confirm(`Remove ${r.mobile} from the do-not-call list? The AI may call them again.`)) return;
        try {
            await myAxios.delete(`call/optouts/${r._id}`);
            toast.success('Removed');
            load(data.page);
        } catch (err) {
            toast.error(errMsg(err, 'Could not remove'));
        }
    };

    return (
        <>
            <div className={styles.card}>
                <h2 className={styles.cardTitle}>Add a number</h2>
                <p className={styles.hint} style={{ marginTop: 6 }}>
                    Customers who say "don't call me" on a marketing call are added automatically. Their own order confirmation calls still happen unless you choose "All AI calls".
                </p>
                <div className={styles.row} style={{ marginTop: 10 }}>
                    <div style={{ maxWidth: 200, flex: '1 1 160px' }}>
                        <input className={styles.input} inputMode="numeric" placeholder="01XXXXXXXXX" value={mobile} onChange={(e) => setMobile(e.target.value)} aria-label="Mobile number" />
                    </div>
                    <div style={{ maxWidth: 220, flex: '1 1 160px' }}>
                        <select className={styles.select} value={scope} onChange={(e) => setScope(e.target.value)} aria-label="Which calls">
                            <option value="marketing">No marketing calls</option>
                            <option value="all">No AI calls at all</option>
                        </select>
                    </div>
                    <button className={styles.btnPrimary} onClick={add} disabled={busy}>{busy ? 'Adding…' : 'Add'}</button>
                </div>
            </div>
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <h2 className={styles.cardTitle}>Do-not-call list ({data.total})</h2>
                    <div style={{ maxWidth: 220, flex: '1 1 160px' }}>
                        <input className={styles.input} placeholder="Search a number" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search" />
                    </div>
                </div>
                {data.rows.length === 0 ? (
                    <div className={styles.empty}>Nobody on the list.</div>
                ) : (
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead><tr><th>Number</th><th>Blocks</th><th>Added</th><th>Why</th><th></th></tr></thead>
                            <tbody>
                                {data.rows.map((r) => (
                                    <tr key={r._id}>
                                        <td className={styles.num}><b>{r.mobile}</b></td>
                                        <td>{r.scope === 'all' ? <span className={`${styles.pill} ${styles.pillRed}`}>All AI calls</span> : <span className={`${styles.pill} ${styles.pillAmber}`}>Marketing</span>}</td>
                                        <td className={styles.num}>{fmtDate(r.at)}<div className={styles.muted}>{r.source === 'call' ? 'asked on a call' : 'by you'}</div></td>
                                        <td>{r.reason || '—'}</td>
                                        <td><button className={styles.btnLink} style={{ color: '#d72c0d' }} onClick={() => remove(r)}>Remove</button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {data.pages > 1 && (
                    <div className={styles.row} style={{ justifyContent: 'flex-end', marginTop: 12 }}>
                        <button className={`${styles.btnSecondary} ${styles.btnSmall}`} disabled={data.page <= 1} onClick={() => load(data.page - 1)}>Previous</button>
                        <span className={styles.muted}>Page {data.page} of {data.pages}</span>
                        <button className={`${styles.btnSecondary} ${styles.btnSmall}`} disabled={data.page >= data.pages} onClick={() => load(data.page + 1)}>Next</button>
                    </div>
                )}
            </div>
        </>
    );
}

export default DoNotCall;
